import React from "react";
import { ButtonProps, useMediaQuery, useTheme } from "@mui/material";

import { ICONS } from "@APP/assets";
import { Message } from "@APP/components/Message";
import { GENERIC_ERROR_TITLE, SERVER_ERROR_MESSAGE } from "@APP/types/errorCodes";
import { DESKTOP_CTA_SX } from "@APP/styles/cta";
import { MOBILE_ICON } from "@APP/styles/mobile";

interface ErrorButton extends ButtonProps {
  text?: string;
}

export interface Props {
  /** Overrides the shared "Something seems to have gone wrong" headline. */
  title?: string;
  /** Body copy inside the white card. Defaults to the generic 5xx wording. */
  description?: string;
  /**
   * Replaces the default single "Close" CTA — pass the journey's own action
   * (e.g. "start again") where the payer still has somewhere to go.
   */
  buttons?: ErrorButton[];
  /**
   * Renders the icon/title/card with no action row, for call sites that supply
   * their own actions — the decline modal, whose footer owns its buttons.
   */
  hideButtons?: boolean;
  style?: React.CSSProperties;
}

/**
 * Lloyds (LBG) generic failure state: the red alert circle, the shared
 * headline, the body copy in a white card and a single right-aligned action
 * under it. Every screen that can fail — bank select, decline, the decline
 * modal — renders this so the states stay identical.
 *
 * The design tucks the CTA straight under the card, so `actionsGap` is 0
 * rather than the roomier `DESKTOP_STATUS.ACTIONS_GAP` default.
 */
const ErrorMessage = ({
  title = GENERIC_ERROR_TITLE,
  description = SERVER_ERROR_MESSAGE,
  buttons,
  hideButtons = false,
  style = { margin: "auto" },
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // The payer reaches these screens from their bank's app or an email client,
  // so there is nowhere left in the journey to send them — closing the tab is
  // the only action. Browsers only honour `window.close()` for script-opened
  // windows, so this is best-effort.
  const closeButton: ErrorButton = {
    text: "Close",
    variant: "contained",
    color: "primary",
    onClick: () => window.close(),
    sx: isMobile ? { marginTop: "16px" } : DESKTOP_CTA_SX,
  };

  return (
    <Message
      style={style}
      type="error"
      title={title}
      description={description}
      iconSrc={ICONS.ERROR_ICON}
      iconSize={isMobile ? MOBILE_ICON.error : undefined}
      buttons={hideButtons ? undefined : buttons ?? [closeButton]}
      actionsGap={0}
    />
  );
};

export default ErrorMessage;
